import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { usePageVisibility } from "@/context/PageVisibilityContext";
import { toast } from "sonner";
import { Eye, EyeOff, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const { reload: reloadVisibility } = usePageVisibility();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pages`, { withCredentials: true });
      setPages(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (page) => {
    setSavingKey(page.page_key);
    try {
      const { data } = await axios.put(
        `${API}/admin/pages/${page.page_key}`,
        { is_visible: !page.is_visible },
        { withCredentials: true },
      );
      setPages((prev) => prev.map((p) => (p.page_key === page.page_key ? data : p)));
      reloadVisibility(); // refresh global visibility map
      toast.success(
        `${page.title} is now ${data.is_visible ? "visible to visitors" : "hidden from visitors"}`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update page");
    } finally {
      setSavingKey(null);
    }
  };

  const visibleCount = pages.filter((p) => p.is_visible).length;
  const hiddenCount = pages.length - visibleCount;

  return (
    <AdminLayout title="Pages">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header card */}
        <div className="bg-white p-6 border border-gray-200" data-testid="admin-pages-header">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-heading text-2xl font-bold uppercase text-[#09090b] mb-1">Page Visibility</h2>
              <p className="text-sm text-gray-500 max-w-2xl">
                Control which pages appear in the public navigation. Hidden pages disappear from the header
                and their URLs redirect visitors to the home page. As an admin, you still see hidden pages —
                marked with an <span className="inline-flex items-center gap-1 text-orange-500 font-semibold"><EyeOff className="w-3 h-3" /> icon</span> — so you can preview work in progress.
              </p>
            </div>
            <div className="flex gap-2">
              <Badge className="bg-[#009E60] text-white hover:bg-[#009E60]" data-testid="badge-visible-count">
                <Eye className="w-3 h-3 mr-1" /> {visibleCount} visible
              </Badge>
              <Badge className="bg-orange-500 text-white hover:bg-orange-500" data-testid="badge-hidden-count">
                <EyeOff className="w-3 h-3 mr-1" /> {hiddenCount} hidden
              </Badge>
              <button
                onClick={load}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Refresh"
                data-testid="btn-refresh-pages"
              >
                <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Rows */}
        <div className="bg-white border border-gray-200 divide-y divide-gray-100">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="p-4"><Skeleton className="h-8 w-full" /></div>
            ))
          ) : pages.length === 0 ? (
            <div className="p-8 text-center text-gray-400 italic">No pages configured</div>
          ) : (
            pages.map((p) => (
              <div
                key={p.page_key}
                className="p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                data-testid={`page-row-${p.page_key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-bold uppercase text-[#09090b]">{p.title}</h3>
                    <code className="text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 text-gray-500">/{p.page_key.replace(/_/g, "-")}</code>
                    {!p.is_visible && (
                      <Badge className="bg-orange-500 text-white text-[10px] hover:bg-orange-500">
                        <EyeOff className="w-2.5 h-2.5 mr-1" /> Hidden
                      </Badge>
                    )}
                  </div>
                  {p.description && (
                    <p className="text-xs text-gray-500 mt-1 truncate">{p.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-500 uppercase tracking-widest">
                    {p.is_visible ? "Visible" : "Hidden"}
                  </span>
                  <Switch
                    checked={p.is_visible}
                    onCheckedChange={() => toggle(p)}
                    disabled={savingKey === p.page_key}
                    data-testid={`switch-${p.page_key}`}
                    className="data-[state=checked]:bg-[#009E60]"
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
